import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { ShippingQuote, Address, OrderItem, Product } from '../types';

// Local GTA delivery flat rate
export const LOCAL_GTA_RATE = 18.00;

// Category thresholds for delivery speed classification
const ECONOMY_MAX_DAYS = 7;
const STANDARD_MAX_DAYS = 4;

interface ShippingRateRequest {
  destination: {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  };
  items: {
    weight: number;
    quantity: number;
  }[];
}

// eShipper API response can vary - handle multiple formats
interface EShipperQuote {
  carrierId?: string;
  carrierName?: string;
  carrier?: string;
  serviceId?: string;
  serviceName?: string;
  service?: string;
  transitDays?: number | null;
  deliveryDays?: number | null;
  totalCharge?: number;
  rate?: number;
  total?: number;
}

interface ShippingRateResponse {
  success: boolean;
  quotes?: EShipperQuote[] | { quotes?: EShipperQuote[] };
  error?: string;
}

// Categorize a quote based on delivery days
const categorizeQuote = (deliveryDays: number | null): 'economy' | 'standard' | 'express' => {
  if (deliveryDays === null) {
    return 'standard'; // Default to standard if unknown
  }
  if (deliveryDays > ECONOMY_MAX_DAYS) {
    return 'economy';
  }
  if (deliveryDays > STANDARD_MAX_DAYS) {
    return 'standard';
  }
  return 'express';
};

// Calculate total weight from cart items
export const calculateTotalWeight = (
  cartItems: OrderItem[],
  products: Product[]
): number => {
  return cartItems.reduce((total, item) => {
    const product = products.find(p => p.id === item.productId);
    const weight = product?.weight || 1.5; // Default weight if not found
    return total + (weight * item.quantity);
  }, 0);
};

// Get best quote per category (cheapest in each category)
const getBestQuotesPerCategory = (quotes: ShippingQuote[]): ShippingQuote[] => {
  const bestByCategory: Record<string, ShippingQuote> = {};

  quotes.forEach(quote => {
    const existing = bestByCategory[quote.category];
    if (!existing || quote.price < existing.price) {
      bestByCategory[quote.category] = quote;
    }
  });

  // Return in order: economy, standard, express
  const result: ShippingQuote[] = [];
  if (bestByCategory.economy) result.push(bestByCategory.economy);
  if (bestByCategory.standard) result.push(bestByCategory.standard);
  if (bestByCategory.express) result.push(bestByCategory.express);

  return result;
};

// Fetch shipping rates from eShipper via Cloud Function
export const getShippingRates = async (
  address: Address,
  cartItems: OrderItem[],
  products: Product[]
): Promise<{ quotes: ShippingQuote[]; error?: string }> => {
  try {
    // Build items array with weight and quantity for each cart item
    const items = cartItems.map(item => {
      const product = products.find(p => p.id === item.productId);
      return {
        weight: product?.weight || 1.5, // Default weight if not found
        quantity: item.quantity
      };
    });

    const request: ShippingRateRequest = {
      destination: {
        address: address.street,
        city: address.city,
        province: address.province,
        postalCode: address.postalCode.replace(/\s/g, '').toUpperCase(),
        country: address.country || 'CA'
      },
      items
    };

    const getShippingRatesFunc = httpsCallable<ShippingRateRequest, ShippingRateResponse>(
      functions,
      'getShippingRates'
    );

    const result = await getShippingRatesFunc(request);

    if (!result.data.success) {
      return {
        quotes: [],
        error: result.data.error || 'No shipping options available for this address'
      };
    }

    // Handle different response formats from eShipper
    let rawQuotes: EShipperQuote[] = [];
    const quotesData = result.data.quotes;

    if (Array.isArray(quotesData)) {
      rawQuotes = quotesData;
    } else if (quotesData && typeof quotesData === 'object' && 'quotes' in quotesData) {
      rawQuotes = (quotesData as { quotes: EShipperQuote[] }).quotes || [];
    }

    if (rawQuotes.length === 0) {
      return {
        quotes: [],
        error: 'No shipping options available for this address'
      };
    }

    // Transform and categorize quotes - handle various field names
    const categorizedQuotes: ShippingQuote[] = rawQuotes.map(q => {
      const price = q.totalCharge || q.rate || q.total || 0;
      const days = q.transitDays ?? q.deliveryDays ?? null;
      return {
        carrierId: q.carrierId || q.carrier || 'unknown',
        carrierName: q.carrierName || q.carrier || 'Carrier',
        serviceId: q.serviceId || q.service || 'standard',
        serviceName: q.serviceName || q.service || 'Standard Service',
        deliveryDays: days,
        price: price,
        category: categorizeQuote(days)
      };
    }).filter(q => q.price > 0);

    // Get best quote per category
    const bestQuotes = getBestQuotesPerCategory(categorizedQuotes);

    return { quotes: bestQuotes };
  } catch (error: any) {
    console.error('Error fetching shipping rates:', error);
    return {
      quotes: [],
      error: error.message || 'Failed to fetch shipping rates. Please try again.'
    };
  }
};

// Check if an address is in the GTA (Greater Toronto Area)
export const isGTAAddress = (postalCode: string): boolean => {
  // GTA postal code prefixes (rough approximation)
  const gtaPrefixes = [
    'L1', 'L3', 'L4', 'L5', 'L6', 'L7', // Surrounding regions
    'M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9' // Toronto
  ];
  const prefix = postalCode.replace(/\s/g, '').toUpperCase().substring(0, 2);
  return gtaPrefixes.includes(prefix);
};
